import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { getIsInTime, isPdrOrder } from '../../helpers/mlkActions';
import { getExpectedServiceDateAndTimeSlot } from '../../helpers/order';
import {
  BlockedReason,
  Order,
  OrderState,
  ShippersConfigInfo,
} from '../../models';
import { useOrder } from '../useOrder';

export const useIsPdrEnabled = () => {
  const order = useOrder();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isPrdAvailable = useMemo(() => {
    if (order && cmsConfigs)
      return getIsPdrEnabled(
        order,
        cmsConfigs.featureFlags.isPdrEnabled,
        cmsConfigs.pdrPreviousDays,
        cmsConfigs.shippersForPreviousDaysPDR,
        cmsConfigs.shippersForSameDayPDR
      );
    return false;
  }, [cmsConfigs, order]);

  return isPrdAvailable;
};

const getIsPdrEnabled = (
  order: Order,
  isServiceEnabled: boolean,
  enableInPreviousDays: number,
  shippersForPreviousDaysPDR: Array<ShippersConfigInfo>,
  shippersForSameDayPDR: Array<ShippersConfigInfo>
) => {
  const isLastAttempt = order.blockedReasons?.includes(BlockedReason.B01);
  const isFulfilled = order.status === OrderState.Fulfilled;
  let isRightTime: boolean = getIsInTime(order, 1);

  let isInDaysRange = false;

  const expectedDateAndTimeSlot = getExpectedServiceDateAndTimeSlot(order);
  const isShipperIdPreviousDay = shippersForPreviousDaysPDR.find(
    (s) => s.shipperId === order.shipper.id
  );
  const isShipperIdForSameDay = shippersForSameDayPDR.find(
    (s) => s.shipperId === order.shipper.id
  );
  if (expectedDateAndTimeSlot) {
    const today = dayjs();
    const daysDifference = expectedDateAndTimeSlot.to
      .hour(0)
      .minute(0)
      .second(0)
      .diff(today, 'days');
    isInDaysRange =
      (daysDifference > 0 &&
        daysDifference <= enableInPreviousDays &&
        !isEmpty(isShipperIdPreviousDay)) ||
      (daysDifference === 0 && !isEmpty(isShipperIdForSameDay));
  }

  const isPdr = isPdrOrder(order);
  const isEditAddressEnabled =
    order.shipperTerritoryConfiguration.uiParams.enableEditAddress;

  return (
    !isLastAttempt &&
    !isFulfilled &&
    isEditAddressEnabled &&
    isInDaysRange &&
    !isPdr &&
    isRightTime &&
    isServiceEnabled
  );
};
